const runtimeEnv = typeof import.meta !== 'undefined' && import.meta && import.meta.env
  ? import.meta.env
  : {}
const API_BASE = (runtimeEnv.VITE_API_BASE_URL || '/api').replace(/\/$/, '')
const TOKEN_KEY = 'cabinets_web_pwa_token'
const THEME_KEY = 'cabinets_web_pwa_theme'
const PROFILE_KEY = 'cabinets_web_pwa_profile'
const UI_MODE_KEY = 'cabinets_web_pwa_ui_mode'
const LOGIN_CHALLENGE_KEY = 'cabinets_web_pwa_login_challenge'
const CURRENT_SITE_KEY = 'cabinets_web_pwa_current_site'
const RECENT_SITES_KEY = 'cabinets_web_pwa_recent_sites'
const SITE_CACHE_SCOPE_SEPARATOR = '::'
const TOKEN_TTL_MS = 180 * 24 * 60 * 60 * 1000
const MAX_RECENT_SITE_CACHE_COUNT = 5
const APPOINTMENTS_CURRENT_CACHE_TTL_MS = 8000
const APPOINTMENTS_HISTORY_CACHE_TTL_MS = 8000
const ASSISTANT_REQUEST_TIMEOUT_MS = 15000
const DEFAULT_UI_MODE = 'appointment'
const VALID_UI_MODES = new Set(['appointment', 'assistant'])
const AUTH_CLIENT_SOURCE = 'h5-pwa'
const OTP_LOGIN_ACTION = 'otp_login_or_register'
let appointmentsCurrentCache = {
  token: '',
  compact: true,
  fetchedAt: 0,
  etag: '',
  value: null,
  promise: null
}
let appointmentsHistoryCache = new Map()

export class ApiError extends Error {
  constructor(message, { status = 0, code = '', details = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

function readToken() {
  try {
    const raw = window.localStorage.getItem(TOKEN_KEY) || ''
    if (!raw) return ''

    let record = null
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && typeof parsed.token === 'string') {
        record = {
          token: parsed.token,
          expiresAt: Number(parsed.expiresAt) || 0
        }
      }
    } catch (error) {
      record = {
        token: raw,
        expiresAt: 0
      }
    }

    if (!record || !record.token) {
      window.localStorage.removeItem(TOKEN_KEY)
      return ''
    }

    if (record.expiresAt && record.expiresAt <= Date.now()) {
      window.localStorage.removeItem(TOKEN_KEY)
      return ''
    }

    if (!record.expiresAt) {
      writeToken(record.token)
    }

    return record.token
  } catch (error) {
    return ''
  }
}

function writeToken(token) {
  try {
    if (!token) {
      window.localStorage.removeItem(TOKEN_KEY)
      return
    }
    window.localStorage.setItem(TOKEN_KEY, JSON.stringify({
      token,
      expiresAt: Date.now() + TOKEN_TTL_MS
    }))
  } catch (error) {
    void error
  }
}

function readTheme() {
  try {
    return window.localStorage.getItem(THEME_KEY) || ''
  } catch (error) {
    return ''
  }
}

function writeTheme(theme) {
  try {
    if (!theme) {
      window.localStorage.removeItem(THEME_KEY)
      return
    }
    window.localStorage.setItem(THEME_KEY, theme)
  } catch (error) {
    void error
  }
}

function readProfile() {
  try {
    const raw = window.localStorage.getItem(PROFILE_KEY) || ''
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? sanitizeProfileForStorage(parsed) : null
  } catch (error) {
    return null
  }
}

function maskPhoneForProfile(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}****${digits.slice(-4)}`
  }
  return String(phone || '').trim()
}

function sanitizeProfileForStorage(profile) {
  if (!profile || typeof profile !== 'object') return null
  const nextProfile = { ...profile }
  const rawPhone = String(nextProfile.phone || '').trim()
  const phoneTail = String(nextProfile.phone_tail || '').replace(/\D/g, '').slice(-4)
    || rawPhone.replace(/\D/g, '').slice(-4)
  const maskedPhone = rawPhone ? maskPhoneForProfile(rawPhone) : ''
  if (rawPhone) {
    nextProfile.phone = maskedPhone
    nextProfile.phone_masked = maskedPhone
  }
  if (phoneTail) {
    nextProfile.phone_tail = phoneTail
  }
  return nextProfile
}

function writeProfile(profile) {
  try {
    if (!profile || typeof profile !== 'object') {
      window.localStorage.removeItem(PROFILE_KEY)
      return
    }
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(sanitizeProfileForStorage(profile)))
  } catch (error) {
    void error
  }
}

function readUiMode() {
  try {
    const raw = String(window.localStorage.getItem(UI_MODE_KEY) || '').trim()
    if (VALID_UI_MODES.has(raw)) {
      return raw
    }
    return DEFAULT_UI_MODE
  } catch (error) {
    return DEFAULT_UI_MODE
  }
}

function writeUiMode(mode) {
  try {
    const normalized = VALID_UI_MODES.has(String(mode || '').trim()) ? String(mode || '').trim() : DEFAULT_UI_MODE
    window.localStorage.setItem(UI_MODE_KEY, normalized)
  } catch (error) {
    void error
  }
}

function getSessionStorage() {
  try {
    return window.sessionStorage || null
  } catch (error) {
    return null
  }
}

function readStorageJsonValue(storageArea, key, fallback) {
  try {
    const raw = storageArea?.getItem(key) || ''
    if (!raw) return fallback
    return JSON.parse(raw)
  } catch (error) {
    return fallback
  }
}

function writeStorageJsonValue(storageArea, key, value) {
  try {
    if (!storageArea) return
    if (value === undefined || value === null) {
      storageArea.removeItem(key)
      return
    }
    storageArea.setItem(key, JSON.stringify(value))
  } catch (error) {
    void error
  }
}

function readLoginChallenge() {
  const sessionStorage = getSessionStorage()
  const value = readStorageJsonValue(sessionStorage, LOGIN_CHALLENGE_KEY, null)
  if (!value || typeof value !== 'object') return null

  const phone = String(value.phone || '').trim()
  const challengeId = String(value.challengeId || value.challenge_id || '').trim()
  const resendAvailableAt = Number(value.resendAvailableAt || value.resend_available_at) || 0

  if (!phone || !challengeId) return null

  return {
    phone,
    challengeId,
    resendAvailableAt
  }
}

function writeLoginChallenge(challenge) {
  const sessionStorage = getSessionStorage()
  if (!challenge || typeof challenge !== 'object') {
    writeStorageJsonValue(sessionStorage, LOGIN_CHALLENGE_KEY, null)
    writeJsonValue(LOGIN_CHALLENGE_KEY, null)
    return null
  }

  const normalized = {
    phone: String(challenge.phone || '').trim(),
    challengeId: String(challenge.challengeId || challenge.challenge_id || '').trim(),
    resendAvailableAt: Number(challenge.resendAvailableAt || challenge.resend_available_at) || 0
  }

  if (!normalized.phone || !normalized.challengeId) {
    writeStorageJsonValue(sessionStorage, LOGIN_CHALLENGE_KEY, null)
    writeJsonValue(LOGIN_CHALLENGE_KEY, null)
    return null
  }

  writeStorageJsonValue(sessionStorage, LOGIN_CHALLENGE_KEY, normalized)
  writeJsonValue(LOGIN_CHALLENGE_KEY, null)
  return normalized
}

function clearLoginChallenge() {
  writeStorageJsonValue(getSessionStorage(), LOGIN_CHALLENGE_KEY, null)
  writeJsonValue(LOGIN_CHALLENGE_KEY, null)
}

function readJsonValue(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key) || ''
    if (!raw) return fallback
    return JSON.parse(raw)
  } catch (error) {
    return fallback
  }
}

function getScopedSiteCacheKey(baseKey, userId) {
  const normalizedUserId = String(userId || '').trim()
  return normalizedUserId ? `${baseKey}${SITE_CACHE_SCOPE_SEPARATOR}${normalizedUserId}` : baseKey
}

function writeJsonValue(key, value) {
  try {
    if (value === undefined || value === null) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    void error
  }
}

function readCurrentSite() {
  const value = readJsonValue(CURRENT_SITE_KEY, null)
  return value && typeof value === 'object' ? value : null
}

function readCurrentSiteForUser(userId) {
  const value = readJsonValue(getScopedSiteCacheKey(CURRENT_SITE_KEY, userId), null)
  return value && typeof value === 'object' ? value : null
}

function writeCurrentSite(site) {
  if (!site || typeof site !== 'object') {
    writeJsonValue(CURRENT_SITE_KEY, null)
    return
  }
  writeJsonValue(CURRENT_SITE_KEY, site)
}

function writeCurrentSiteForUser(userId, site) {
  const scopedKey = getScopedSiteCacheKey(CURRENT_SITE_KEY, userId)
  if (!site || typeof site !== 'object') {
    writeJsonValue(scopedKey, null)
    return
  }
  writeJsonValue(scopedKey, site)
}

function readRecentSites() {
  const value = readJsonValue(RECENT_SITES_KEY, [])
  if (!Array.isArray(value)) return []
  return value.filter(Boolean).slice(0, MAX_RECENT_SITE_CACHE_COUNT)
}

function readRecentSitesForUser(userId) {
  const value = readJsonValue(getScopedSiteCacheKey(RECENT_SITES_KEY, userId), [])
  if (!Array.isArray(value)) return []
  return value.filter(Boolean).slice(0, MAX_RECENT_SITE_CACHE_COUNT)
}

function writeRecentSites(recentSites) {
  if (!Array.isArray(recentSites)) {
    writeJsonValue(RECENT_SITES_KEY, [])
    return []
  }
  const normalized = recentSites.filter(Boolean).slice(0, MAX_RECENT_SITE_CACHE_COUNT)
  writeJsonValue(RECENT_SITES_KEY, normalized)
  return normalized
}

function writeRecentSitesForUser(userId, recentSites) {
  const scopedKey = getScopedSiteCacheKey(RECENT_SITES_KEY, userId)
  if (!Array.isArray(recentSites)) {
    writeJsonValue(scopedKey, [])
    return []
  }
  const normalized = recentSites.filter(Boolean).slice(0, MAX_RECENT_SITE_CACHE_COUNT)
  writeJsonValue(scopedKey, normalized)
  return normalized
}

function clearSiteCache() {
  try {
    const storageArea = window.localStorage
    storageArea.removeItem(CURRENT_SITE_KEY)
    storageArea.removeItem(RECENT_SITES_KEY)
    storageArea.removeItem('pending_scanned_site')
    if (storageArea && typeof storageArea.length === 'number' && typeof storageArea.key === 'function') {
      const keysToRemove = []
      for (let i = 0; i < storageArea.length; i++) {
        const key = storageArea.key(i)
        if (key && (
          key.startsWith(`${CURRENT_SITE_KEY}${SITE_CACHE_SCOPE_SEPARATOR}`) ||
          key.startsWith(`${RECENT_SITES_KEY}${SITE_CACHE_SCOPE_SEPARATOR}`)
        )) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => storageArea.removeItem(key))
    }
  } catch (error) {
    void error
  }
}

function invalidateAppointmentsCurrentCache() {
  appointmentsCurrentCache = {
    token: '',
    compact: true,
    fetchedAt: 0,
    etag: '',
    value: null,
    promise: null
  }
}

function invalidateAppointmentsHistoryCache() {
  appointmentsHistoryCache = new Map()
}

function buildCacheQueryKey(params = {}) {
  const query = params && typeof params === 'object' ? params : {}
  const entries = Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
  return JSON.stringify(entries)
}

function clearSiteCacheForUser(userId) {
  const scopedCurrentKey = getScopedSiteCacheKey(CURRENT_SITE_KEY, userId)
  const scopedRecentKey = getScopedSiteCacheKey(RECENT_SITES_KEY, userId)
  try {
    window.localStorage.removeItem(scopedCurrentKey)
    window.localStorage.removeItem(scopedRecentKey)
  } catch (error) {
    void error
  }
}

function buildUrl(path, query) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${API_BASE}${normalizedPath}`, window.location.origin)
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      url.searchParams.set(key, String(value))
    })
  }
  return url
}

function base64UrlToUint8Array(value) {
  const padding = '='.repeat((4 - String(value || '').length % 4) % 4)
  const base64 = `${String(value || '')}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let index = 0; index < rawData.length; index += 1) {
    output[index] = rawData.charCodeAt(index)
  }
  return output
}

function parseSseChunk(buffer, onEvent) {
  const blocks = String(buffer || '').split(/\n\n+/)
  const rest = blocks.pop() || ''
  blocks.forEach((block) => {
    const lines = block.split(/\n/)
    let eventName = 'message'
    const dataLines = []
    lines.forEach((line) => {
      if (!line || line.startsWith(':')) return
      const separatorIndex = line.indexOf(':')
      const field = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line
      const value = separatorIndex >= 0 ? line.slice(separatorIndex + 1).replace(/^ /, '') : ''
      if (field === 'event') eventName = value || 'message'
      if (field === 'data') dataLines.push(value)
    })
    const dataText = dataLines.join('\n')
    let data = dataText
    if (dataText) {
      try {
        data = JSON.parse(dataText)
      } catch (error) {
        data = dataText
      }
    }
    onEvent?.({ type: eventName, data })
  })
  return rest
}

async function subscribeToSse(path, { signal, onEvent, onError } = {}) {
  const token = readToken()
  const requestHeaders = new Headers({
    Accept: 'text/event-stream'
  })
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  try {
    const response = await fetch(buildUrl(path), {
      method: 'GET',
      headers: requestHeaders,
      credentials: 'include',
      signal
    })
    if (!response.ok) {
      throw new ApiError(`事件流连接失败 (${response.status})`, { status: response.status })
    }
    if (!response.body?.getReader) {
      throw new ApiError('当前浏览器不支持事件流')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (!signal?.aborted) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      buffer = parseSseChunk(buffer, onEvent)
    }
  } catch (error) {
    if (signal?.aborted) return
    onError?.(error)
    throw error
  }
}

async function request(path, {
  method = 'GET',
  data = null,
  query = null,
  skipAuth = false,
  headers = {},
  timeoutMs = 0
} = {}) {
  const url = buildUrl(path, method === 'GET' ? (query || data) : null)
  const requestHeaders = new Headers(headers)
  if (!requestHeaders.has('Content-Type') && method !== 'GET') {
    requestHeaders.set('Content-Type', 'application/json')
  }
  if (!skipAuth) {
    const token = readToken()
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }
  }

  const timeout = Number(timeoutMs) || 0
  const controller = timeout > 0 && typeof AbortController !== 'undefined'
    ? new AbortController()
    : null
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), timeout)
    : 0

  const debugLabel = `[API Request] ${method} ${url.pathname}`
  const startedAt = Date.now()

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      credentials: 'include',
      body: method === 'GET' ? undefined : JSON.stringify(data || {}),
      signal: controller?.signal
    })

    let payload = null
    try {
      payload = await response.json()
    } catch (error) {
      payload = null
    }

    if (!response.ok) {
      console.error(`${debugLabel} - HTTP Error Status: ${response.status}`, payload);
      const message = payload?.error || payload?.message || `请求失败 (${response.status})`
      const err = new ApiError(message, {
        status: response.status,
        code: payload?.code || '',
        details: payload?.details || payload?.data || null
      })
      throw err
    }

    if (payload && payload.success === false) {
      console.error(`${debugLabel} - Business Failure Status: ${response.status}`, payload);
      throw new ApiError(payload.error || payload.message || '请求失败', {
        status: response.status,
        code: payload.code || '',
        details: payload.details || payload.data || null
      })
    }

    console.log(`${debugLabel} - Success`, {
      status: response.status,
      durationMs: Date.now() - startedAt,
      url: url.toString(),
      query,
      payload
    })
    return payload || {}
  } catch (error) {
    if (error?.name === 'AbortError') {
      console.error(`${debugLabel} - Aborted/Timeout`, {
        durationMs: Date.now() - startedAt,
        url: url.toString(),
        query
      })
      throw new ApiError('请求超时，请稍后重试', { code: 'REQUEST_TIMEOUT' })
    }
    console.error(`${debugLabel} - Network/Internal Error`, {
      durationMs: Date.now() - startedAt,
      url: url.toString(),
      query,
      error
    })
    throw error
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

function requestAppointmentHistory(params = {}, { force = false } = {}) {
  const token = readToken()
  const cacheKey = `${token}::${buildCacheQueryKey(params)}`
  const existingEntry = appointmentsHistoryCache.get(cacheKey) || {
    fetchedAt: 0,
    etag: '',
    value: null,
    promise: null
  }

  const hasFreshValue = (
    !force &&
    existingEntry.value &&
    Date.now() - existingEntry.fetchedAt < APPOINTMENTS_HISTORY_CACHE_TTL_MS
  )

  if (hasFreshValue) {
    return Promise.resolve(existingEntry.value)
  }

  if (!force && existingEntry.promise) {
    return existingEntry.promise
  }

  const url = buildUrl('/appointments/my', params)
  const requestHeaders = new Headers()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }
  if (existingEntry.etag) {
    requestHeaders.set('If-None-Match', existingEntry.etag)
  }

  const nextPromise = fetch(url, {
    method: 'GET',
    headers: requestHeaders,
    credentials: 'include'
  })
    .then(async (response) => {
      if (response.status === 304 && existingEntry.value) {
        appointmentsHistoryCache.set(cacheKey, {
          ...existingEntry,
          fetchedAt: Date.now(),
          promise: null
        })
        return existingEntry.value
      }

      let payload = null
      try {
        payload = await response.json()
      } catch (error) {
        payload = null
      }

      if (!response.ok) {
        const message = payload?.error || payload?.message || `请求失败 (${response.status})`
        const err = new ApiError(message, {
          status: response.status,
          code: payload?.code || '',
          details: payload?.details || payload?.data || null
        })
        throw err
      }

      if (payload && payload.success === false) {
        throw new ApiError(payload.error || payload.message || '请求失败', {
          status: response.status,
          code: payload.code || '',
          details: payload.details || payload.data || null
        })
      }

      return {
        payload: payload || {},
        etag: String(response.headers.get('etag') || '').trim()
      }
    })
    .then((result) => {
      const payload = result?.payload || result
      const etag = typeof result?.etag === 'string' ? result.etag : existingEntry.etag
      appointmentsHistoryCache.set(cacheKey, {
        fetchedAt: Date.now(),
        etag,
        value: payload,
        promise: null
      })
      return payload
    })
    .catch((error) => {
      appointmentsHistoryCache.set(cacheKey, {
        ...existingEntry,
        promise: null
      })
      throw error
    })

  appointmentsHistoryCache.set(cacheKey, {
    ...existingEntry,
    promise: nextPromise
  })
  return nextPromise
}

function requestCurrentAppointments({ force = false, compact = true } = {}) {
  const token = readToken()

  if (appointmentsCurrentCache.token !== token || appointmentsCurrentCache.compact !== compact) {
    invalidateAppointmentsCurrentCache()
    appointmentsCurrentCache.token = token
    appointmentsCurrentCache.compact = compact
  }

  const hasFreshValue = (
    !force &&
    appointmentsCurrentCache.value &&
    Date.now() - appointmentsCurrentCache.fetchedAt < APPOINTMENTS_CURRENT_CACHE_TTL_MS
  )

  if (hasFreshValue) {
    return Promise.resolve(appointmentsCurrentCache.value)
  }

  if (!force && appointmentsCurrentCache.promise) {
    return appointmentsCurrentCache.promise
  }

  const url = buildUrl('/appointments/current', compact ? { compact: 1 } : null)
  const requestHeaders = new Headers()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }
  if (appointmentsCurrentCache.etag) {
    requestHeaders.set('If-None-Match', appointmentsCurrentCache.etag)
  }

  const nextPromise = fetch(url, {
    method: 'GET',
    headers: requestHeaders,
    credentials: 'include'
  })
    .then(async (response) => {
      if (response.status === 304 && appointmentsCurrentCache.value) {
        appointmentsCurrentCache = {
          ...appointmentsCurrentCache,
          token,
          compact,
          fetchedAt: Date.now(),
          promise: null
        }
        return appointmentsCurrentCache.value
      }

      let payload = null
      try {
        payload = await response.json()
      } catch (error) {
        payload = null
      }

      if (!response.ok) {
        const message = payload?.error || payload?.message || `请求失败 (${response.status})`
        const err = new ApiError(message, {
          status: response.status,
          code: payload?.code || '',
          details: payload?.details || payload?.data || null
        })
        throw err
      }

      if (payload && payload.success === false) {
        throw new ApiError(payload.error || payload.message || '请求失败', {
          status: response.status,
          code: payload.code || '',
          details: payload.details || payload.data || null
        })
      }

      return {
        payload: payload || {},
        etag: String(response.headers.get('etag') || '').trim()
      }
    })
    .then((result) => {
      const payload = result?.payload || result
      const nextEtag = typeof result?.etag === 'string' ? result.etag : appointmentsCurrentCache.etag
      appointmentsCurrentCache = {
        token,
        compact,
        fetchedAt: Date.now(),
        etag: nextEtag,
        value: payload,
        promise: null
      }
      return payload
    })
    .catch((error) => {
      appointmentsCurrentCache.promise = null
      throw error
    })

  appointmentsCurrentCache.promise = nextPromise
  return nextPromise
}

export const storage = {
  readToken,
  writeToken,
  readTheme,
  writeTheme,
  readProfile,
  writeProfile,
  readUiMode,
  writeUiMode,
  readLoginChallenge,
  writeLoginChallenge,
  clearLoginChallenge,
  readCurrentSite,
  writeCurrentSite,
  readCurrentSiteForUser,
  writeCurrentSiteForUser,
  readRecentSites,
  writeRecentSites,
  readRecentSitesForUser,
  writeRecentSitesForUser,
  clearSiteCache,
  clearSiteCacheForUser,
  clearSession() {
    invalidateAppointmentsCurrentCache()
    invalidateAppointmentsHistoryCache()
    writeToken('')
    clearLoginChallenge()
  }
}

export const api = {
  auth: {
    me() {
      return request('/auth/me')
    },
    updateProfile(data) {
      return request('/auth/profile', {
        method: 'PUT',
        data
      })
    },
    logout() {
      return request('/auth/logout', { method: 'POST', data: {} })
    },
    deleteAccount() {
      return request('/auth/account', { method: 'DELETE', data: {} })
    },
    sendLoginCode(phone) {
      return request('/auth/verification/challenges', {
        method: 'POST',
        data: {
          action: OTP_LOGIN_ACTION,
          phone,
          client_source: AUTH_CLIENT_SOURCE
        },
        skipAuth: true
      })
    },
    verifyLoginCode({ phone, code, challengeId }) {
      return request(`/auth/verification/challenges/${encodeURIComponent(challengeId)}/verify`, {
        method: 'POST',
        data: {
          phone,
          code,
          client_source: AUTH_CLIENT_SOURCE
        },
        skipAuth: true
      })
    },
    consumeVerificationProof(proofToken) {
      return request('/auth/verification/consume', {
        method: 'POST',
        data: {
          action: OTP_LOGIN_ACTION,
          proof_token: proofToken,
          client_source: AUTH_CLIENT_SOURCE
        },
        skipAuth: true
      })
    },
    identifyLogin({ siteId, phoneTail, matchType, matchValue }) {
      return request('/auth/identify/login', {
        method: 'POST',
        data: {
          site_id: siteId,
          phone_tail: phoneTail,
          match_type: matchType,
          match_value: matchValue,
          client_source: AUTH_CLIENT_SOURCE
        },
        skipAuth: true
      })
    },
    identifyBindSite(siteId) {
      return request('/auth/identify/bind-site', {
        method: 'POST',
        data: {
          site_id: siteId
        }
      })
    }
  },
  users: {
    syncCurrentSitePreferences() {
      return request('/users/preferences/current-site')
    },
    getCurrentSite() {
      return request('/users/preferences/current-site')
    },
    setCurrentSite(siteId) {
      return request('/users/preferences/current-site', {
        method: 'PUT',
        data: { siteId }
      })
    }
  },
  appointments: {
    current(options = {}) {
      const normalizedOptions = options && typeof options === 'object' ? options : {}
      const compact = normalizedOptions.compact !== false
      return requestCurrentAppointments({ ...normalizedOptions, compact })
    },
    my(params = {}) {
      const query = params && typeof params === 'object' ? params : {}
      const force = query.force === true
      const normalizedQuery = { ...query }
      delete normalizedQuery.force
      return requestAppointmentHistory(normalizedQuery, { force })
    },
    create(data) {
      return request('/appointments', {
        method: 'POST',
        data
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    cancel(id, reason = '') {
      return request(`/appointments/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        data: { reason }
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    dismissInvalid(id) {
      return request(`/appointments/${encodeURIComponent(id)}/dismiss-invalid`, {
        method: 'PUT',
        data: {}
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    dismissExpired(id) {
      return request(`/appointments/${encodeURIComponent(id)}/dismiss-expired`, {
        method: 'PUT',
        data: {}
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    dismissEviction(id) {
      return request(`/appointments/${encodeURIComponent(id)}/dismiss-eviction`, {
        method: 'PUT',
        data: {}
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    confirmPickedUpByUser(id) {
      return request(`/appointments/${encodeURIComponent(id)}/user-confirm-pickup`, {
        method: 'PATCH',
        data: {}
      }).then((payload) => {
        invalidateAppointmentsCurrentCache()
        invalidateAppointmentsHistoryCache()
        return payload
      })
    },
    availableSlots(siteId) {
      return request(`/appointments/available-slots/${encodeURIComponent(siteId)}`)
    },
    subscribeToEvents(options = {}) {
      return subscribeToSse('/appointments/events/stream', options)
    }
  },
  sites: {
    getById(siteId) {
      return request(`/sites/${encodeURIComponent(siteId)}`)
    },
    getByCode(siteCode) {
      return request(`/sites/by-code/${encodeURIComponent(String(siteCode || '').trim())}`)
    },
    scanSiteQRCode(qrData) {
      return request('/sites/scan', {
        method: 'POST',
        data: {
          qrData: String(qrData || '').trim()
        }
      })
    },
    userConfig(siteId) {
      return request(`/sites/${encodeURIComponent(siteId)}/user-config`)
    },
    latestAnnouncement(siteId) {
      return request(`/sites/${encodeURIComponent(siteId)}/announcements/latest`)
    }
  },
  assistant: {
    listConversations(params = {}, options = {}) {
      return request('/pwa-assistant/conversations', {
        query: params,
        timeoutMs: options.timeoutMs ?? ASSISTANT_REQUEST_TIMEOUT_MS
      })
    },
    createConversation(data = {}, options = {}) {
      return request('/pwa-assistant/conversations', {
        method: 'POST',
        data,
        timeoutMs: options.timeoutMs ?? ASSISTANT_REQUEST_TIMEOUT_MS
      })
    },
    getMessages(conversationId, params = {}, options = {}) {
      return request(`/pwa-assistant/conversations/${encodeURIComponent(conversationId)}/messages`, {
        query: params,
        timeoutMs: options.timeoutMs ?? ASSISTANT_REQUEST_TIMEOUT_MS
      })
    },
    sendMessage(conversationId, data = {}, options = {}) {
      return request(`/pwa-assistant/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        data,
        timeoutMs: options.timeoutMs ?? ASSISTANT_REQUEST_TIMEOUT_MS
      })
    },
    archiveConversation(conversationId, options = {}) {
      return request(`/pwa-assistant/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'DELETE',
        data: {},
        timeoutMs: options.timeoutMs ?? ASSISTANT_REQUEST_TIMEOUT_MS
      })
    }
  },
  agreements: {
    getPrivacy() {
      return request('/agreements/privacy', { method: 'GET', skipAuth: true })
    }
  },
  notifications: {
    getPushPublicKey() {
      return request('/notifications/push-public-key')
    },
    savePushSubscription(subscription) {
      return request('/notifications/push-subscriptions', {
        method: 'POST',
        data: subscription
      })
    },
    removePushSubscription(subscription) {
      return request('/notifications/push-subscriptions/remove', {
        method: 'POST',
        data: {
          endpoint: subscription?.endpoint || ''
        }
      })
    },
    urlBase64ToUint8Array: base64UrlToUint8Array
  }
}
